/**
 * Social & Trends API endpoints
 * Twitter, Reddit, News, Telegram, Discord, LunarCrush, Influencer
 */

import { request } from './core';

// ============================================
// TWITTER
// ============================================

export async function getTwitterTrends(woeid = '1') {
  return request(`/api/twitter/trends?woeid=${woeid}`);
}

export async function searchTwitter(query, maxResults = 100) {
  return request(`/api/twitter/search?q=${encodeURIComponent(query)}&max_results=${maxResults}`);
}

export async function getTwitterMentions(symbol, ca = '') {
  const params = new URLSearchParams({ symbol });
  if (ca) params.append('ca', ca);
  return request(`/api/twitter/mentions?${params}`);
}

// ============================================
// REDDIT
// ============================================

export async function getRedditTrends(sort = 'hot', limit = 10) {
  return request(`/api/reddit/trends?sort=${sort}&limit=${limit}`);
}

export async function searchReddit(query, sort = 'relevance', limit = 25) {
  return request(`/api/reddit/search?q=${encodeURIComponent(query)}&sort=${sort}&limit=${limit}`);
}

// ============================================
// NEWS
// ============================================

export async function getNewsHeadlines(category = 'technology', country = 'us') {
  return request(`/api/news/headlines?category=${category}&country=${country}`);
}

export async function searchNews(query, sortBy = 'publishedAt') {
  return request(`/api/news/search?q=${encodeURIComponent(query)}&sortBy=${sortBy}`);
}

export async function getFullArticle(url) {
  return request('/api/news/article', {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
}

// ============================================
// TELEGRAM ALPHA SCANNER
// ============================================

export async function getTelegramStatus() {
  return request('/api/telegram/status');
}

export async function searchTelegramAlpha(query = null, limit = 20) {
  const params = new URLSearchParams({ limit });
  if (query) params.append('query', query);
  return request(`/api/telegram/alpha/search?${params}`);
}

export async function getTelegramTrending() {
  return request('/api/telegram/alpha/trending');
}

export async function getTelegramChannels() {
  return request('/api/telegram/channels');
}

export async function getTelegramMentions(symbol, ca = '') {
  const params = new URLSearchParams({ symbol: (symbol || '').trim() });
  if (ca) params.append('ca', ca);
  return request(`/api/telegram/mentions?${params}`);
}

export async function getTelegramChannelMessages(channel, limit = 50) {
  return request(`/api/telegram/channels/${encodeURIComponent(channel)}/messages?limit=${limit}`);
}

// ============================================
// DISCORD ALPHA SCANNER
// ============================================

export async function getDiscordStatus() {
  return request('/api/discord/status');
}

export async function getDiscordChannelMessages(guildId, channelId, limit = 50) {
  return request(`/api/discord/channels/${guildId}/${channelId}/messages?limit=${limit}`);
}

export async function searchDiscordMessages(guildId, channelId, query, limit = 20) {
  return request(
    `/api/discord/channels/${guildId}/${channelId}/search?q=${encodeURIComponent(query)}&limit=${limit}`
  );
}

export async function getDiscordTrending(guildId, channelIds = [], limit = 10) {
  const params = new URLSearchParams({ guildId, limit });
  if (channelIds.length > 0) params.append('channelIds', channelIds.join(','));
  return request(`/api/discord/trending?${params}`);
}

export async function getDiscordGuildInfo(guildId) {
  return request(`/api/discord/guild/${guildId}`);
}

// ============================================
// LUNARCRUSH (Social Sentiment)
// ============================================

export async function getLunarCrushTrending(limit = 50) {
  return request(`/api/lunarcrush/trending?limit=${limit}`);
}

export async function getLunarCrushCoin(symbol) {
  return request(`/api/lunarcrush/coin/${symbol}`);
}

export async function searchLunarCrush(query) {
  return request(`/api/lunarcrush/search?q=${encodeURIComponent(query)}`);
}

// ============================================
// INFLUENCER TRACKER
// ============================================

export async function getInfluencerProfile(username) {
  return request(`/api/influencer/profile/${username}`);
}

export async function searchInfluencerMentions(query, minFollowers = 5000) {
  return request(
    `/api/influencer/search?q=${encodeURIComponent(query)}&minFollowers=${minFollowers}`
  );
}

export async function trackInfluencer(username, notes = '') {
  return request('/api/influencer/track', {
    method: 'POST',
    body: JSON.stringify({ username, notes }),
  });
}

export async function getTrackedInfluencers() {
  return request('/api/influencer/tracked');
}

export async function untrackInfluencer(username) {
  return request(`/api/influencer/track/${username}`, { method: 'DELETE' });
}

export async function getInfluencerLeaderboard() {
  return request('/api/influencer/leaderboard');
}

// ============================================
// SOCIAL POSTING
// ============================================

export async function getSocialPostStatus() {
  return request('/api/socialpost/status');
}

export async function postToSocial({
  content,
  platforms,
  telegramChatId,
  discordWebhookUrl,
  discordEmbed,
}) {
  return request('/api/socialpost/post', {
    method: 'POST',
    body: JSON.stringify({ content, platforms, telegramChatId, discordWebhookUrl, discordEmbed }),
  });
}

export async function postToTwitterOnly(content) {
  return request('/api/socialpost/twitter', {
    method: 'POST',
    body: JSON.stringify({ content }),
  });
}

export async function postToTelegramOnly(content, chatId, parseMode) {
  return request('/api/socialpost/telegram', {
    method: 'POST',
    body: JSON.stringify({ content, chatId, parseMode }),
  });
}

export async function postToDiscordOnly(content, webhookUrl, embed) {
  return request('/api/socialpost/discord', {
    method: 'POST',
    body: JSON.stringify({ content, webhookUrl, embed }),
  });
}

export async function schedulePost({
  content,
  platforms,
  scheduledTime,
  telegramChatId,
  discordWebhookUrl,
}) {
  return request('/api/socialpost/schedule', {
    method: 'POST',
    body: JSON.stringify({ content, platforms, scheduledTime, telegramChatId, discordWebhookUrl }),
  });
}

export async function getScheduledPosts() {
  return request('/api/socialpost/scheduled');
}

export async function cancelScheduledPost(postId) {
  return request(`/api/socialpost/schedule/${postId}`, { method: 'DELETE' });
}

export async function getPostHistory(limit = 50) {
  return request(`/api/socialpost/history?limit=${limit}`);
}
